import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import SubscriptionService from '../../services/subscription.service';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface DebugInfo {
  reference?: string | null;
  isAuthenticated?: boolean;
  isContribution?: boolean;
  timestamp?: string;
  searchParams?: Record<string, string>;
  localStorage?: {
    token?: boolean;
    pendingVerificationEmail?: string | null;
    contributionCoupleUrl?: string | null;
    isContribution?: string | null;
    paystack_reference?: string | null;
  };
  publicVerifyResponse?: any;
  authenticatedVerifyResponse?: any;
  userResponse?: any;
  publicVerifyError?: string;
  authVerifyError?: string;
  finalError?: {
    message?: string;
    response?: any;
    status?: number;
  };
  [key: string]: any;
}

const PaystackCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Verifying your payment...');
  const [redirectMessage, setRedirectMessage] = useState('');
  const [debugInfo, setDebugInfo] = useState<DebugInfo>({});

  useEffect(() => {
    const reference = searchParams.get('reference');
    const isContribution =
      searchParams.get('isContribution') === 'true' || localStorage.getItem('isContribution') === 'true';

    // Create searchParams object
    const searchParamsObj: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      searchParamsObj[key] = value;
    });

    // Initialize debug info
    setDebugInfo({
      reference: reference,
      isAuthenticated: isAuthenticated,
      isContribution: isContribution,
      timestamp: new Date().toISOString(),
      searchParams: searchParamsObj,
      localStorage: {
        token: !!localStorage.getItem('token'),
        pendingVerificationEmail: localStorage.getItem('pendingVerificationEmail'),
        contributionCoupleUrl: localStorage.getItem('contribution_couple_url'),
        isContribution: localStorage.getItem('isContribution'),
        paystack_reference: localStorage.getItem('paystack_reference'),
      },
    });

    if (!reference) {
      setStatus('error');
      setMessage('No payment reference found');
      toast.error('No payment reference found');
      return;
    }

    const verifyPayment = async () => {
      try {
        if (isContribution) {
          // Redirect contributions to ContributionVerification
          console.log('Redirecting contribution to /contribution-verification with reference:', reference);
          setStatus('loading');
          setMessage('Redirecting to contribution verification...');
          setRedirectMessage('Redirecting...');

          // Preserve necessary query parameters
          const redirectParams = new URLSearchParams({
            reference: reference,
            isContribution: 'true',
          });

          // Redirect to ContributionVerification
          setTimeout(() => {
            navigate(`/contribution-verify?${redirectParams.toString()}`);
          }, 1000);
          return;
        }

        // Handle subscriptions
        let email: string | undefined;
        let needsVerification: boolean | undefined;
        let userId: string | undefined;
        let premiumPlan: string | undefined;
        const pendingEmail = localStorage.getItem('pendingVerificationEmail');

        if (!isAuthenticated) {
          // Non-authenticated subscription verification
          console.log('Verifying subscription (non-authenticated) with reference:', reference);
          try {
            const verifyResponse = await SubscriptionService.verifyPublic(reference);
            console.log('Public subscription verification response:', verifyResponse.data);
            setDebugInfo((prev: DebugInfo) => ({ ...prev, publicVerifyResponse: verifyResponse.data }));

            if (!verifyResponse.data.success) {
              throw new Error(verifyResponse.data.message || 'Public subscription verification failed');
            }

            email = verifyResponse.data.email || pendingEmail;
            needsVerification = verifyResponse.data.needsVerification;
            userId = verifyResponse.data.userId;
            premiumPlan = verifyResponse.data.premiumPlan;

            setStatus('success');
            setMessage(`Payment successful! Your ${premiumPlan || 'subscription'} has been activated.`);
            toast.success('Subscription verified successfully!');

            if (needsVerification || pendingEmail) {
              setRedirectMessage('Redirecting you to verify your email...');
              if (email && !pendingEmail) {
                localStorage.setItem('pendingVerificationEmail', email);
              }
              setTimeout(() => {
                navigate('/verification-required');
              }, 2000);
            } else {
              setRedirectMessage('Redirecting you to login...');
              setTimeout(() => {
                navigate('/login', {
                  state: { email, message: 'Please log in to access your dashboard' },
                });
              }, 2000);
            }
            return;
          } catch (publicVerifyError: any) {
            console.warn('Public subscription verification failed:', publicVerifyError);
            setDebugInfo((prev: DebugInfo) => ({
              ...prev,
              publicVerifyError: publicVerifyError.message || 'Unknown error',
            }));
            throw new Error(`Public subscription verification failed: ${publicVerifyError.message}`);
          }
        }

        // Authenticated subscription verification
        console.log('Verifying subscription (authenticated) with reference:', reference);
        try {
          const verifyResponse = await SubscriptionService.verify(reference);
          console.log('Authenticated subscription verification response:', verifyResponse.data);
          setDebugInfo((prev: DebugInfo) => ({ ...prev, authenticatedVerifyResponse: verifyResponse.data }));

          if (!verifyResponse.data.success) {
            throw new Error(verifyResponse.data.message || 'Authenticated subscription verification failed');
          }

          userId = verifyResponse.data.user?.id || verifyResponse.data.userId;
          email = verifyResponse.data.user?.email || verifyResponse.data.email || pendingEmail;
          needsVerification = verifyResponse.data.user?.needsVerification || verifyResponse.data.needsVerification;
          premiumPlan = verifyResponse.data.user?.premiumPlan || verifyResponse.data.premiumPlan;

          if (!userId) {
            throw new Error('No user ID provided in payment verification response');
          }

          // Fetch user details
          try {
            const userResponse = await api.get(`/users/${userId}`);
            console.log('User details response:', userResponse.data);
            setDebugInfo((prev: DebugInfo) => ({ ...prev, userResponse: userResponse.data }));

            email = userResponse.data.email || email;
            needsVerification = !userResponse.data.isVerified || needsVerification;
            premiumPlan = userResponse.data.premiumPlan || premiumPlan;
          } catch (userFetchError) {
            console.warn('Failed to fetch user details:', userFetchError);
          }

          setStatus('success');
          setMessage(`Payment successful! Your ${premiumPlan || 'subscription'} has been activated.`);
          toast.success('Subscription verified successfully!');

          if (needsVerification || pendingEmail) {
            setRedirectMessage('Redirecting you to verify your email...');
            if (email && !pendingEmail) {
              localStorage.setItem('pendingVerificationEmail', email);
            }
            setTimeout(() => {
              navigate('/verification-required');
            }, 2000);
          } else {
            setRedirectMessage('Redirecting you to your dashboard...');
            setTimeout(() => {
              navigate('/admin');
            }, 2000);
          }
        } catch (authVerifyError: any) {
          console.error('Authenticated subscription verification failed:', authVerifyError);
          setDebugInfo((prev: DebugInfo) => ({
            ...prev,
            authVerifyError: authVerifyError.message || 'Unknown error',
          }));
          throw new Error(`Authenticated subscription verification failed: ${authVerifyError.message}`);
        }
      } catch (error: any) {
        console.error('Payment verification error:', {
          reference: reference,
          isContribution: isContribution,
          isAuthenticated: isAuthenticated,
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        });

        setDebugInfo((prev: DebugInfo) => ({
          ...prev,
          finalError: {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
          },
        }));

        setStatus('error');
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'An error occurred while verifying your payment';
        setMessage(errorMessage);
        toast.error(errorMessage);
      }
    };

    verifyPayment();
  }, [searchParams, navigate, isAuthenticated]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
        {status === 'loading' && (
          <>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
            </div>
            <h2 className="mt-6 text-center text-xl font-medium text-gray-900">{message}</h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {redirectMessage || 'Please wait while we confirm your payment...'}
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="flex justify-center">
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="mt-6 text-center text-xl font-medium text-gray-900">{message}</h2>
            <p className="mt-2 text-center text-sm text-gray-600">{redirectMessage}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex justify-center">
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <h2 className="mt-6 text-center text-xl font-medium text-gray-900">Payment Verification Failed</h2>
            <p className="mt-2 text-center text-sm text-gray-600">{message}</p>
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => navigate(debugInfo.isContribution ? '/' : '/register')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
              >
                {debugInfo.isContribution ? 'Return to Homepage' : 'Return to Register'}
              </button>
            </div>
          </>
        )}

        {process.env.NODE_ENV !== 'production' && (
          <div className="mt-8 text-left border-t border-gray-200 pt-4">
            <details className="text-xs">
              <summary className="text-gray-500 cursor-pointer">Debug Information</summary>
              <pre className="mt-2 p-2 bg-gray-50 rounded overflow-auto max-h-60 text-gray-700">
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaystackCallback;